// Header section: tenant logo + newsletter title + subtitle + cover image.
// In the visual builder this is edit-overlay style (no inline controls);
// the entire surface flips to a fullscreen-style edit Card when active.

import { Box, Button, Card, CircularProgress, TextField, Typography } from "@mui/material"
import { InlineAiGenerator } from "@crm-src/components/inline-ai-generator"
import { AiTextImprover } from "@crm-src/components/ai-text-improver"
import { Type, Upload, X } from "lucide-react"
import { NewsletterSection, TenantInfo, headerImageOptions, toAbsoluteUrl, toPreviewImageUrl } from "./stock-images"

// The XOP brand mark shown in the header — "the ball" (the navy + lime orbit
// mark, no wordmark). We deliberately render this rather than the tenant's
// `logo_url`: the full XOP wordmark logo squeezed into the 50px header slot
// looked cramped and was redundant with the "XOP Networks" name printed right
// next to it. The clean lockup is [ball] XOP Networks.
//
// It's a PNG (NOT `xop-mark.svg`) because mail clients drop SVG, and it lives
// in the support center's bundled `/stock-images/` so it resolves on the
// public host in the preview AND is CID-inlined into the sent email
// (`EmailImageInliner`, xop-brain) — same path every other newsletter image
// takes. Asset: xop-edge-customer/apps/portal-web/public/stock-images/xop-ball.png.
const XOP_BRAND_MARK_URL = "/stock-images/xop-ball.png"

interface HeaderSectionProps {
  section: NewsletterSection
  isEditing: boolean
  onUpdate: (sectionId: string, partial: any) => void
  setEditingSection: (id: string | null) => void
  tenantName: string
  tenantLogo?: string | null
  tenantInfo?: TenantInfo
  // The orchestrator owns the upload state and ref so multiple header
  // sections (in theory) wouldn't fight over the same file input. In
  // practice there's only ever one header, but the lift-state contract
  // keeps the component pure.
  customHeaderImages: Array<{ id: number; url: string; name: string }>
  isUploadingHeaderImage: boolean
  headerImageInputRef: React.RefObject<HTMLInputElement | null>
  onHeaderImageUpload: (event: React.ChangeEvent<HTMLInputElement>, sectionId: string) => void
  onDeleteCustomImage: (imageId: number, imageUrl: string, event: React.MouseEvent) => void
}

export function HeaderSection({
  section,
  isEditing,
  onUpdate,
  setEditingSection,
  tenantName,
  tenantLogo,
  tenantInfo,
  customHeaderImages,
  isUploadingHeaderImage,
  headerImageInputRef,
  onHeaderImageUpload,
  onDeleteCustomImage,
}: HeaderSectionProps) {
  return (
    <Box
      className="group"
      sx={{ position: "relative", p: 0, overflow: "hidden" }}
    >
      {/* Edit overlay — appears on hover when not in edit mode */}
      {!isEditing && (
        <Box sx={{ position: "absolute", inset: 0, transition: "all 0.15s", display: "flex", alignItems: "center", justifyContent: "center", opacity: 0, zIndex: 10, ".group:hover &": { opacity: 1 } }}>
          <Box sx={{ position: "absolute", inset: 0, bgcolor: "#000", opacity: 0, transition: "all 0.15s", ".group:hover &": { opacity: 0.2 } }} />
          <Button
            size="small"
            onClick={() => setEditingSection(section.id)}
            startIcon={<Type size={16} />}
            sx={{ bgcolor: "#fff", color: "text.primary", "&:hover": { bgcolor: "action.hover" }, position: "relative", zIndex: 10 }}
          >
            Edit Header
          </Button>
        </Box>
      )}

      {isEditing ? (
        <Card sx={{ m: 2, display: "flex", flexDirection: "column", maxHeight: "calc(100vh - 200px)" }}>
          {/* Scrollable content area */}
          <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2, overflowY: "auto", flex: 1 }}>
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                <Typography component="label" sx={{ fontSize: 14, fontWeight: 500 }}>Header Title</Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <InlineAiGenerator
                    fieldType="short"
                    context="newsletter header title"
                    placeholder="e.g., monthly technology update"
                    currentValue={section.content.title}
                    onGenerate={(content) => onUpdate(section.id, { title: content })}
                  />
                  <AiTextImprover
                    fieldType="short"
                    context="newsletter header title"
                    currentText={section.content.title}
                    onImprove={(content) => onUpdate(section.id, { title: content })}
                  />
                </Box>
              </Box>
              <TextField
                fullWidth
                size="small"
                value={section.content.title}
                onChange={(e) => onUpdate(section.id, { title: e.target.value })}
                sx={{ "& .MuiInputBase-input": { fontSize: "1.25rem", fontWeight: 700 } }}
              />
            </Box>
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                <Typography component="label" sx={{ fontSize: 14, fontWeight: 500 }}>Date (Subtitle)</Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <InlineAiGenerator
                    fieldType="short"
                    context="newsletter date or subtitle"
                    placeholder="e.g., current month and year"
                    currentValue={section.content.subtitle}
                    onGenerate={(content) => onUpdate(section.id, { subtitle: content })}
                  />
                  <AiTextImprover
                    fieldType="short"
                    context="newsletter date or subtitle"
                    currentText={section.content.subtitle}
                    onImprove={(content) => onUpdate(section.id, { subtitle: content })}
                  />
                </Box>
              </Box>
              <TextField
                fullWidth
                size="small"
                value={section.content.subtitle}
                onChange={(e) => onUpdate(section.id, { subtitle: e.target.value })}
                placeholder="e.g., October 2025"
              />
            </Box>
            <Box>
            <Typography component="label" sx={{ fontSize: "1rem", fontWeight: 600 }}>Select Header Image</Typography>
            <Typography sx={{ fontSize: 14, color: "text.secondary", mb: 1.5 }}>Choose a stock image or upload your own</Typography>

            {/* Custom Images Section — uploaded by the user, persisted server-side */}
            {customHeaderImages.length > 0 && (
              <Box sx={{ mb: 1.5 }}>
                <Typography component="label" sx={{ fontSize: 14, color: "text.secondary", mb: 1, display: "block" }}>Your Uploaded Images</Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 1.5 }}>
                  {customHeaderImages.map((option) => (
                    <Box key={option.id} className="group" sx={{ position: "relative" }}>
                      <Box
                        component="button"
                        onClick={() => onUpdate(section.id, { imageUrl: option.url })}
                        sx={{
                          position: "relative",
                          height: 96,
                          width: "100%",
                          borderRadius: 1,
                          border: "2px solid",
                          transition: "all 0.15s",
                          "&:hover": { transform: "scale(1.05)", boxShadow: 3 },
                          overflow: "hidden",
                          bgcolor: "#1f2937",
                          cursor: "pointer",
                          ...(section.content.imageUrl === option.url
                            ? { borderColor: "success.main", boxShadow: (t) => `0 0 0 2px ${t.palette.success.main}` }
                            : { borderColor: "#d1d5db" }),
                        }}
                      >
                        <Box
                          component="img"
                          src={toPreviewImageUrl(option.url)}
                          alt={option.name}
                          loading="eager"
                          onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                            (e.target as HTMLImageElement).style.display = 'none'
                          }}
                          sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
                        />
                        <Box sx={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(0,0,0,0.6), transparent)", display: "flex", alignItems: "flex-end", p: 1, zIndex: 10 }}>
                          <Box component="span" sx={{ color: "#fff", fontSize: 12, fontWeight: 500, textShadow: "0 1px 2px rgba(0,0,0,0.4)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{option.name}</Box>
                        </Box>
                        <Box sx={{ position: "absolute", top: 4, left: 4, bgcolor: "primary.main", color: "#fff", borderRadius: 0.5, px: 0.75, py: 0.25, zIndex: 20 }}>
                          <Box component="span" sx={{ fontSize: 10, fontWeight: 600 }}>Custom</Box>
                        </Box>
                        {section.content.imageUrl === option.url && (
                          <Box sx={{ position: "absolute", top: 4, right: 4, bgcolor: "success.main", color: "#fff", borderRadius: "50%", p: 0.5, zIndex: 20, display: "flex" }}>
                            <svg width={16} height={16} fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </Box>
                        )}
                      </Box>
                      {/* Delete button — only on the user's own uploads, not stock images */}
                      <Box
                        component="button"
                        onClick={(e: React.MouseEvent) => onDeleteCustomImage(option.id, option.url, e)}
                        title="Delete image"
                        sx={{ position: "absolute", top: -8, right: -8, bgcolor: "error.main", color: "#fff", borderRadius: "50%", p: 0.75, opacity: 0, transition: "opacity 0.15s", boxShadow: 3, zIndex: 30, cursor: "pointer", border: "none", display: "flex", "&:hover": { bgcolor: "error.dark" }, ".group:hover &": { opacity: 1 } }}
                      >
                        <X size={12} />
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Stock Images Section — bundled in /public/stock-images/ */}
            <Typography component="label" sx={{ fontSize: 14, color: "text.secondary", mb: 1, display: "block" }}>Stock Images</Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 1.5, mb: 1.5 }}>
              {headerImageOptions.map((option) => (
                <Box
                  component="button"
                  key={option.id}
                  onClick={() => onUpdate(section.id, { imageUrl: option.url })}
                  sx={{
                    position: "relative",
                    height: 96,
                    borderRadius: 1,
                    border: "2px solid",
                    transition: "all 0.15s",
                    "&:hover": { transform: "scale(1.05)", boxShadow: 3 },
                    overflow: "hidden",
                    bgcolor: "#1f2937",
                    cursor: "pointer",
                    ...(section.content.imageUrl === option.url
                      ? { borderColor: "success.main", boxShadow: (t) => `0 0 0 2px ${t.palette.success.main}` }
                      : { borderColor: "#d1d5db" }),
                  }}
                >
                  <Box
                    component="img"
                    src={`${toPreviewImageUrl(option.url)}?w=400&h=200&fit=crop&auto=format`}
                    alt={option.name}
                    loading="eager"
                    onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                      // Fallback if image fails to load
                      (e.target as HTMLImageElement).style.display = 'none'
                    }}
                    sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
                  />
                  <Box sx={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(0,0,0,0.6), transparent)", display: "flex", alignItems: "flex-end", p: 1, zIndex: 10 }}>
                    <Box component="span" sx={{ color: "#fff", fontSize: 12, fontWeight: 500, textShadow: "0 1px 2px rgba(0,0,0,0.4)" }}>{option.name}</Box>
                  </Box>
                  {section.content.imageUrl === option.url && (
                    <Box sx={{ position: "absolute", top: 4, right: 4, bgcolor: "success.main", color: "#fff", borderRadius: "50%", p: 0.5, zIndex: 20, display: "flex" }}>
                      <svg width={16} height={16} fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </Box>
                  )}
                </Box>
              ))}
            </Box>

            <input
              ref={headerImageInputRef}
              type="file"
              accept="image/*"
              onChange={(e) => onHeaderImageUpload(e, section.id)}
              style={{ display: "none" }}
            />

            <Button
              type="button"
              variant="outlined"
              color="inherit"
              fullWidth
              onClick={() => headerImageInputRef.current?.click()}
              disabled={isUploadingHeaderImage}
              startIcon={isUploadingHeaderImage ? <CircularProgress size={16} color="inherit" /> : <Upload size={16} />}
            >
              {isUploadingHeaderImage ? "Uploading Custom Image..." : "Upload Custom Image"}
            </Button>
            </Box>
          </Box>

          {/* Fixed Save button at bottom */}
          <Box sx={{ p: 2, borderTop: 1, borderColor: "divider", bgcolor: "background.paper" }}>
            <Button size="small" fullWidth color="success" variant="contained" onClick={() => setEditingSection(null)}>Save</Button>
          </Box>
        </Card>
      ) : (
        <>
          {/* Top bar with logo and date */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            padding: '25px 40px 15px 40px',
            backgroundColor: '#D9DABF'
          }}>
            {/* Left side - Logo and Tenant Name */}
            <div style={{ textAlign: 'left' }}>
              {/* Brand mark ("the ball") and Tenant Name in one row */}
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
                <img
                  src={toPreviewImageUrl(XOP_BRAND_MARK_URL)}
                  alt={tenantInfo?.name || tenantName}
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                  style={{ height: '50px', width: 'auto', marginRight: '15px', display: 'block' }}
                />
                <p style={{
                  color: '#2C5015',
                  fontSize: '24px',
                  margin: 0,
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif',
                  fontWeight: '700',
                  letterSpacing: '0.5px',
                  textShadow: '0 1px 2px rgba(0,0,0,0.1)',
                  whiteSpace: 'nowrap'
                }}>
                  {tenantInfo?.name || tenantName}
                </p>
              </div>
            </div>

            {/* Right side - Date */}
            <div style={{ textAlign: 'right' }}>
              <p style={{
                color: '#2C5015',
                fontSize: '16px',
                margin: 0,
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif'
              }}>
                {section.content.subtitle}
              </p>
            </div>
          </div>

          {/* Newsletter Title - Centered */}
          <div style={{
            padding: '0 40px 25px 40px',
            textAlign: 'center',
            backgroundColor: '#D9DABF'
          }}>
            <h1 style={{
              color: '#2C5015',
              fontSize: '38px',
              margin: 0,
              fontWeight: '700',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif',
              letterSpacing: '-0.5px'
            }}>
              {section.content.title}
            </h1>
          </div>

          {/* Header Image Section */}
          <div style={{
            position: 'relative',
            height: '200px',
            lineHeight: 0,
            fontSize: 0,
            margin: 0,
            padding: 0,
            overflow: 'hidden'
          }}>
            <img
              src={toPreviewImageUrl(section.content.imageUrl || '/stock-images/conference-room.jpg')}
              alt="Header Image"
              style={{
                width: '100%',
                height: '200px',
                display: 'block',
                objectFit: 'cover',
                margin: 0,
                padding: 0,
                verticalAlign: 'bottom',
                border: 0
              }}
            />
          </div>
        </>
      )}
    </Box>
  )
}

// HTML serializer used by the orchestrator's `generateEmailHTML`. Output
// is byte-for-byte the same as the original monolithic file.
export function headerToHtml(
  section: NewsletterSection,
  tenantName: string,
  // `tenantLogo` is kept in the signature for caller compatibility but no longer
  // drives the header image — we always render the XOP brand mark (the ball),
  // not the squeezed full wordmark. See XOP_BRAND_MARK_URL above.
  _tenantLogo: string | null | undefined,
  tenantInfo: TenantInfo | undefined
): string {
  // The brand mark ("the ball"), anchored absolute to the support host so the
  // mail client (and EmailImageInliner) can fetch it.
  const brandMarkUrl = toAbsoluteUrl(XOP_BRAND_MARK_URL)

  return `
          <!-- Header -->
          <tr>
            <td style="background-color: #D9DABF; padding: 0;">
              <!-- Top section with logo and date (light green background) -->
              <table width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color: #D9DABF;">
                <tr>
                  <td style="padding: 25px 40px 15px 40px;">
                    <table width="100%" cellpadding="0" cellspacing="0" border="0">
                      <tr>
                        <td style="width: 50%; vertical-align: top; text-align: left;">
                          <!-- Logo and Tenant Name -->
                          <table cellpadding="0" cellspacing="0" border="0">
                            <tr>
                              <td style="vertical-align: middle; padding-right: 15px;">
                                <img src="${brandMarkUrl}" alt="${tenantInfo?.name || tenantName}" height="50" style="height: 50px; width: auto; display: block; border: 0;" />
                              </td>
                              <td style="vertical-align: middle; white-space: nowrap;">
                                <p style="color: #2C5015; font-size: 24px; margin: 0; font-family: Arial, Helvetica, sans-serif; font-weight: bold; letter-spacing: 0.5px; white-space: nowrap;">${tenantInfo?.name || tenantName}</p>
                              </td>
                            </tr>
                          </table>
                        </td>
                        <td style="width: 50%; vertical-align: top; text-align: right;">
                          <p style="color: #2C5015; font-size: 16px; margin: 0; font-family: Arial, Helvetica, sans-serif;">${section.content.subtitle}</p>
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>
              </table>
              
              <!-- Newsletter Title (same light green background) -->
              <table width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color: #D9DABF;">
                <tr>
                  <td style="padding: 0 40px 25px 40px; text-align: center;">
                    <h1 style="color: #2C5015; font-size: 38px; margin: 0; font-weight: bold; font-family: Arial, Helvetica, sans-serif; letter-spacing: -0.5px;">${section.content.title}</h1>
                  </td>
                </tr>
              </table>
              
              <!-- Header Image Section -->
              <table width="100%" cellpadding="0" cellspacing="0" border="0">
                <tr>
                  <td style="padding: 0; line-height: 0;">
                    <img src="${toAbsoluteUrl(section.content.imageUrl || '/stock-images/conference-room.jpg')}" alt="Header Image" width="650" height="200" style="width: 100%; height: auto; max-height: 200px; display: block; border: 0;" />
                  </td>
                </tr>
              </table>
            </td>
          </tr>
`
}
